import { For, Show } from "solid-js";
import { cn } from "@famidex/shared";

export interface ChartSegment {
  label: string;
  value: number;
  /** Any CSS color: a theme token (e.g. "var(--success)") or hex. */
  color: string;
}

interface DonutChartProps {
  segments: ChartSegment[];
  /** Diameter in px. */
  size?: number;
  /** Ring thickness in px. */
  thickness?: number;
  /** Big number rendered in the center. */
  centerValue?: string | number;
  /** Small caption under the center value. */
  centerLabel?: string;
  /** Accessible textual summary (role="img"). Auto-generated if omitted. */
  summary?: string;
  /** Translated label used when there are no segments (a11y fallback). */
  emptyLabel?: string;
  class?: string;
}

/**
 * Theme-aware SVG donut chart. Pure SVG/CSS (no chart library). Accessible:
 * exposes `role="img"` with a textual summary so screen readers convey the
 * data without seeing the graphic. Colors are passed by the caller as CSS
 * values (theme tokens recommended) so it stays theme-safe.
 */
export function DonutChart(props: DonutChartProps) {
  const size = () => props.size ?? 140;
  const thickness = () => props.thickness ?? 14;
  const radius = () => (size() - thickness()) / 2;
  const circumference = () => 2 * Math.PI * radius();

  const total = () => props.segments.reduce((acc, s) => acc + Math.max(0, s.value), 0);

  // Precompute dash offsets so segments are drawn end-to-end around the ring.
  const arcs = () => {
    const c = circumference();
    const t = total();
    let cumulative = 0;
    return props.segments.map((seg) => {
      const fraction = t > 0 ? Math.max(0, seg.value) / t : 0;
      const dash = fraction * c;
      const offset = cumulative * c;
      cumulative += fraction;
      return { ...seg, dash, gap: c - dash, offset };
    });
  };

  const autoSummary = () =>
    props.segments.map((s) => `${s.label}: ${s.value}`).join(", ") || (props.emptyLabel ?? "");

  return (
    <div
      role="img"
      aria-label={props.summary ?? autoSummary()}
      class={cn("relative inline-flex items-center justify-center", props.class)}
      style={{ width: `${size()}px`, height: `${size()}px` }}
    >
      <svg
        width={size()}
        height={size()}
        viewBox={`0 0 ${size()} ${size()}`}
        class="-rotate-90"
        aria-hidden="true"
      >
        {/* Track */}
        <circle
          cx={size() / 2}
          cy={size() / 2}
          r={radius()}
          fill="none"
          stroke="currentColor"
          class="text-muted/40"
          stroke-width={thickness()}
        />
        <Show when={total() > 0} fallback={null}>
          <For each={arcs()}>
            {(arc) => (
              <circle
                cx={size() / 2}
                cy={size() / 2}
                r={radius()}
                fill="none"
                stroke={arc.color}
                stroke-width={thickness()}
                stroke-linecap="round"
                stroke-dasharray={`${arc.dash} ${arc.gap}`}
                stroke-dashoffset={-arc.offset}
                class="transition-[stroke-dasharray,stroke-dashoffset] duration-700 ease-out"
              />
            )}
          </For>
        </Show>
      </svg>
      <Show when={props.centerValue !== undefined || props.centerLabel}>
        <div class="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <Show when={props.centerValue !== undefined}>
            <span class="text-2xl font-black leading-none tabular-nums">{props.centerValue}</span>
          </Show>
          <Show when={props.centerLabel}>
            <span class="mt-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              {props.centerLabel}
            </span>
          </Show>
        </div>
      </Show>
    </div>
  );
}

interface BarChartProps {
  segments: ChartSegment[];
  /** Accessible textual summary (role="img"). Auto-generated if omitted. */
  summary?: string;
  /** Translated label used when there are no segments (a11y fallback). */
  emptyLabel?: string;
  class?: string;
}

/**
 * Theme-aware horizontal bar chart. Each row shows a label, a proportional
 * bar (scaled to the max value), and the raw value. Pure CSS bars, no library.
 * Accessible via `role="img"` + textual summary.
 */
export function BarChart(props: BarChartProps) {
  const maxValue = () => Math.max(1, ...props.segments.map((s) => Math.max(0, s.value)));
  const autoSummary = () =>
    props.segments.map((s) => `${s.label}: ${s.value}`).join(", ") || (props.emptyLabel ?? "");

  return (
    <div
      role="img"
      aria-label={props.summary ?? autoSummary()}
      class={cn("flex flex-col gap-3", props.class)}
    >
      <For each={props.segments}>
        {(seg) => {
          const pct = () => (Math.max(0, seg.value) / maxValue()) * 100;
          return (
            <div class="flex items-center gap-3">
              <span class="w-20 shrink-0 truncate text-xs font-medium text-muted-foreground">
                {seg.label}
              </span>
              <div class="h-2.5 flex-1 overflow-hidden rounded-full bg-muted/40">
                <div
                  class="h-full origin-left rounded-full transition-transform duration-700 ease-out"
                  style={{
                    width: "100%",
                    transform: `scaleX(${pct() / 100})`,
                    "background-color": seg.color,
                  }}
                />
              </div>
              <span class="w-8 shrink-0 text-right text-xs font-bold tabular-nums">
                {seg.value}
              </span>
            </div>
          );
        }}
      </For>
    </div>
  );
}
